import { useCallback, useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'
import { replace } from 'connected-react-router'
import ExperienceSearchPageFilters from './ExperienceSearchPageFilters'
import FilterPanelFilterGroup from 'components/Common/FilterPanel/FilterPanelFilterGroup'
import ExperiencesSortByOptions from './ExperiencesSortByOptions'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import useExperiencesSearchFilters from 'components/Experiences/hooks/useExperiencesSearchFilters'
import { useAppDispatch } from 'hooks/reduxHooks'
import useQueryParams from 'hooks/useQueryParams'

interface Props {
  open?: boolean;
  onClose?: () => void;
  place?: App.Place;
}

function ExperienceSearchPageFiltersSidebar(props: Props) {
  const { open, onClose, place } = props
  const intl = useIntl()
  const { setSearchFilters } = useExperiencesSearchFilters()
  const dispatch = useAppDispatch()
  const queryParams = useQueryParams()
  const urlSnapshot = useRef<string | null>(null)

  useEffect(() => {
    if (open) {
      urlSnapshot.current = queryParams.toString()
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const handleApply = useCallback(() => {
    urlSnapshot.current = null
    onClose?.()
  }, [onClose])

  const handleDismiss = useCallback(() => {
    if (urlSnapshot.current !== null) {
      dispatch(replace({ search: urlSnapshot.current }))
      urlSnapshot.current = null
    }
    onClose?.()
  }, [dispatch, onClose])

  const onClearAll = useCallback(() => {
    setSearchFilters({})
  }, [setSearchFilters])

  return (
    <ModalBase mode="drawer" isOpen={open} onClose={handleDismiss}>
      <ModalHeader
        title={intl.formatMessage({
          id: '-sHD19_YfrtV2KBci1xt',
          defaultMessage: 'Sort & filter',
          description:
            'Title for the sort and filter sidebar modal in experience search',
        })}
        onCloseButtonClick={handleDismiss}
      />
      <ModalBody>
        <ModalContent>
          <FilterPanelFilterGroup
            title={intl.formatMessage({
              id: 'iD2zuFMw3T1Y-PseOsOg',
              defaultMessage: 'Sort by',
              description:
                'Title for the sort options group in experience search filter sidebar',
            })}
          >
            <ExperiencesSortByOptions />
          </FilterPanelFilterGroup>
          <ExperienceSearchPageFilters place={place} />
        </ModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          children: intl.formatMessage({
            id: 'XGAxBfjicX0x40wpm7Xt',
            defaultMessage: 'Apply',
            description:
              'Button text to apply sort and filter selections in experience search',
          }),
          onClick: handleApply,
          style: { flexGrow: 2 },
        }}
        secondaryActionProps={{
          children: intl.formatMessage({
            id: 'MHb8lQ7Liy1ixnu8EKVa',
            defaultMessage: 'Clear all',
            description:
              'Action button label to clear all applied flight filters',
          }),
          onClick: onClearAll,
          style: { flexGrow: 0 },
        }}
      />
    </ModalBase>
  )
}

export default ExperienceSearchPageFiltersSidebar
